import { render, html } from 'lit-html';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { router } from './router.js';
import { authService } from './services/auth.js';
import { firebaseService } from './services/firebase.js';
import { userService } from './services/user.js';
import { agencyService } from './services/agency.js';
import { clientService } from './services/client.js';
import { themeService } from './services/theme.js';
import { userContextService } from './services/user-context.js';
import './services/anonymous-mode.js'; // Anonymous mode for screenshots
import { notificationContainer } from './components/notifications.js';
import './components/offline-banner.js'; // Offline status banner
import { appFooter } from './components/app-footer.js';
import { dashboardHeader } from './components/dashboard-header.js';
import { animatedBackground } from './components/animated-background.js';
import { localeService, t } from './i18n/index.js';
import { logger } from './utils/logger.js';
import { seoService } from './services/seo.js';

// ==========================================
// Global Error Handler - Captura erros nao tratados
// ==========================================
window.addEventListener('error', (event) => {
  logger.error('Uncaught error:', event.error?.message || event.message);
  // Evitar crash total - nao propagar
});

window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled promise rejection:', event.reason?.message || event.reason);
  // Evitar crash total
  event.preventDefault();
});

// Inicializar i18n PRIMEIRO (síncrono para garantir traduções no preloader)
try {
  await localeService.init();
} catch (e) {
  logger.error('Failed to init i18n, using defaults');
}

// Initialize SEO base meta tags
seoService.initBaseMeta();

// Atualizar texto inicial do preloader após i18n carregar
const initialStatus = document.querySelector('#preloader-status .status-text');
if (initialStatus) {
  initialStatus.textContent = t('preloader.loadingStyles');
}

let stylesLoaded = false;
let stylesLoadedPromise = null;

// Helper para atualizar progresso do preloader
const updateProgress = (percent, text) => {
  if (window.updatePreloaderProgress) {
    window.updatePreloaderProgress(percent, text);
  }
};

// Lazy load non-critical CSS to improve initial load performance
// Critical CSS is already inlined in index.html
const loadStyles = async () => {
  if (stylesLoaded) return;

  updateProgress(5);

  // Load essential styles in parallel
  const stylePromises = [
    import('./styles/base.css'),      // Base reset and normalize styles
    import('./styles/theme.css'),     // CSS variables and theme definitions
    import('./styles/loading-core.css'), // Unified loading system (spinners, skeletons, overlays)
    import('./styles/utilities.css'), // Utility classes (focus, hover, accessibility)
    import('./styles/glassmorphism.css'), // Glass morphism effects
    import('./styles/buttons.css'),   // Global button components
    import('./styles/dashboard-header.css'), // Dashboard header with neon animations
    import('./styles/dashboard.css'), // Dashboard styles (loaded globally to prevent timing issues)
    import('./styles/welcome.css'),   // Welcome page styles after subscription
    import('./styles/login.css'),     // Login page styles
  ];

  await Promise.all(stylePromises);
  updateProgress(20);

  // Load dashboard-enhanced after essential styles (lower priority)
  await import('./styles/dashboard-enhanced.css');

  stylesLoaded = true;
  updateProgress(30, t('preloader.connectingServices'));
};

// Start loading styles immediately and cache the promise
stylesLoadedPromise = loadStyles().catch((e) => {
  logger.error('Failed to load styles:', e);
});

// Firebase configuration - use environment variables in production
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validar config essencial antes de inicializar
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  throw new Error('Firebase config missing required fields (apiKey, projectId)');
}

const app = initializeApp(firebaseConfig);

// Analytics pode falhar (ad blockers) - nao bloquear app
try {
  getAnalytics(app);
} catch (e) {
  logger.warn('Analytics initialization failed (possibly blocked)');
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export { app };

updateProgress(40, t('preloader.verifyingAuth'));

authService.initialize(auth);
firebaseService.initialize(db);
userContextService.initialize();

// i18n já inicializado no início do arquivo

updateProgress(50);

// ==========================================
// Dynamic Viewport Height for Mobile
// Define variáveis CSS para altura do viewport (útil para layouts)
// A bottom nav usa position:fixed + bottom:0 e não precisa dessas variáveis
// ==========================================
const setDynamicViewportHeight = () => {
  const vh = window.visualViewport?.height || window.innerHeight;
  document.documentElement.style.setProperty('--app-height', `${vh}px`);
  document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
};

// Executar imediatamente
setDynamicViewportHeight();

// Atualizar apenas em resize e orientação (não precisa em scroll)
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', setDynamicViewportHeight);
}
window.addEventListener('resize', setDynamicViewportHeight);
window.addEventListener('orientationchange', () => {
  setTimeout(setDynamicViewportHeight, 150);
});

window.authService = authService;
window.userService = userService;
window.agencyService = agencyService;
window.clientService = clientService;
window.localeService = localeService;

themeService.subscribe(() => {
  renderApp();
});

localeService.subscribe(() => {
  renderApp();
});

const appContainer = document.getElementById('app');

let currentRoute = null;
let transitionTimeout = null;
let isTransitioning = false;

authService.onAuthStateChanged(async (user) => {
  updateProgress(70, t('preloader.preparingInterface'));
  renderApp();

  // Verificar migração de criptografia para agências autenticadas
  if (user) {
    try {
      const agency = userService.getCurrentAgency();
      if (agency?.id) {
        const { encryptionMigrationService } = await import('./utils/encryption.js');
        await encryptionMigrationService.checkAndNotifyMigration(agency.id);
      }
    } catch (e) {
      // Silenciar erros - não bloquear fluxo por falha de verificação
    }
  }
});

function shouldShowHeader() {
  const currentPath = router.currentPath;
  // Don't show header on landing, login, signup, register, public pages, blog, and public client login pages
  const noHeaderPaths = ['/', '/login', '/signup', '/register', '/clients/login/', '/help-center', '/status', '/privacy-policy', '/terms-of-service', '/cookie-policy', '/documentation', '/blog', '/support'];
  return !noHeaderPaths.some(path =>
    currentPath === path || currentPath.startsWith('/clients/login/') || currentPath.startsWith('/blog')
  );
}

function shouldShowAppFooter() {
  const currentPath = router.currentPath;
  // Don't show app footer on landing page (has its own footer)
  // Also hide on blog pages (have their own footer)
  if ((currentPath === '/' && !authService.currentUser) || currentPath.startsWith('/blog')) {
    return false;
  }
  return true;
}

async function renderApp() {
  try {
    // Safety check for container
    if (!appContainer) {
      logger.error('App container not found');
      return;
    }

    // Show minimal loading state while styles or i18n are loading
    if (!stylesLoaded || !localeService.isInitialized()) {
      // Use a very simple placeholder without spinner
      const loadingTemplate = html`
        <div class="app-loading-minimal">
          <style>
            .app-loading-minimal {
              min-height: 100vh;
              background: linear-gradient(180deg, #f8f9fa 0%, #f1f3f5 100%);
            }
            [data-theme="dark"] .app-loading-minimal {
              background: linear-gradient(180deg, #1a1a1a 0%, #121212 100%);
            }
          </style>
        </div>
      `;
      render(loadingTemplate, appContainer);

      // Wait for styles to load (i18n already initialized at startup)
      await stylesLoadedPromise;
    }

    // Sinalizar que o app está pronto (remove preloader) - AFTER styles loaded
    if (authService.isInitialized && stylesLoaded && !document.body.classList.contains('app-ready')) {
      updateProgress(100, t('preloader.ready'));

      setTimeout(() => {
        requestAnimationFrame(() => {
          document.body.classList.add('app-ready');
        });
      }, 300);
    }

    // Cancel any pending transition
    if (transitionTimeout) {
      clearTimeout(transitionTimeout);
      transitionTimeout = null;
    }

    // Force reset if transitioning for too long (safety mechanism)
    if (isTransitioning) {
      isTransitioning = false;
    }

    const newRoute = router.currentPath;

    // Detecta se está saindo da página de perfil do cliente e reseta os filtros
    if (currentRoute && currentRoute.startsWith('/clients/profile/') &&
        newRoute && !newRoute.startsWith('/clients/profile/')) {
      import('./pages/client-profile-page.js').then(module => {
        if (module.resetClientProfileFilters) {
          module.resetClientProfileFilters();
        }
      }).catch(() => {});
    }

    // Detecta se está saindo da página de clientes e reseta o estado de carregamento
    if (currentRoute === '/clients' && newRoute && newRoute !== '/clients') {
      import('./pages/clients-page.js').then(module => {
        if (module.resetClientsPageLoadState) {
          module.resetClientsPageLoadState();
        }
      }).catch(() => {});
    }

    // Clean up landing page body class when navigating away (CSS :has() fallback)
    if (currentRoute === '/' && newRoute !== '/') {
      document.body.classList.remove('landing-page-active');
    }

    // Always render the content, with or without transition
    const shouldTransition = currentRoute && currentRoute !== newRoute && !isTransitioning;

    if (shouldTransition) {
      // Mark as transitioning
      isTransitioning = true;

      // Get current content element
      const oldContent = document.querySelector('.app-content');

      if (oldContent) {
        // Start fade out
        oldContent.classList.remove('page-transition-active');
        oldContent.classList.add('page-transition-out');
      }

      // Schedule new content render with shorter delay
      transitionTimeout = setTimeout(() => {
        // Render new content
        const appTemplate = html`
          ${animatedBackground()}
          <a href="#main-content" class="skip-to-content">${t('common.skipToContent') || 'Pular para o conteúdo'}</a>
          <div class="app-wrapper">
            ${shouldShowHeader() ? dashboardHeader() : ''}
            <div class="app-content page-transition-in" id="main-content">
              ${router.render()}
            </div>
            ${shouldShowAppFooter() ? appFooter() : ''}
          </div>
        `;

        render(appTemplate, appContainer);
        currentRoute = newRoute;

        // Trigger fade in
        requestAnimationFrame(() => {
          const newContent = document.querySelector('.app-content');
          if (newContent) {
            // Remove ALL transition classes first
            newContent.classList.remove('page-transition-in', 'page-transition-out', 'page-transition-active');
            newContent.classList.add('page-transition-active');
          }
          isTransitioning = false;
        });

        transitionTimeout = null;
      }, 120); // Faster transition (was 200ms, now 120ms)

    } else {
      // Direct render without transition
      const appTemplate = html`
        ${animatedBackground()}
        <a href="#main-content" class="skip-to-content">${t('common.skipToContent') || 'Pular para o conteúdo'}</a>
        <div class="app-wrapper">
          ${shouldShowHeader() ? dashboardHeader() : ''}
          <div class="app-content page-transition-active" id="main-content">
            ${router.render()}
          </div>
          ${shouldShowAppFooter() ? appFooter() : ''}
        </div>
      `;

      render(appTemplate, appContainer);
      currentRoute = newRoute;
      isTransitioning = false;

      // Ensure no stuck transition classes
      requestAnimationFrame(() => {
        const content = document.querySelector('.app-content');
        if (content) {
          // Remove any stuck transition classes
          content.classList.remove('page-transition-in', 'page-transition-out');
        }
        // Marca o app como carregado para exibir elementos que dependem do carregamento
        document.body.classList.add('app-loaded');
      });
    }

    // Always render notifications
    const notificationTemplate = notificationContainer();
    render(notificationTemplate, document.body, { host: 'notifications' });

  } catch (error) {
    logger.error('Error in renderApp:', error);
    // Reset transition state
    isTransitioning = false;
    if (transitionTimeout) {
      clearTimeout(transitionTimeout);
      transitionTimeout = null;
    }

    // Fallback rendering without transitions - always show content
    const fallbackTemplate = html`
      ${animatedBackground()}
      <a href="#main-content" class="skip-to-content">${t('common.skipToContent') || 'Pular para o conteúdo'}</a>
      <div class="app-wrapper">
        ${shouldShowHeader() ? dashboardHeader() : ''}
        <div class="app-content page-transition-active" id="main-content">
          ${router.render()}
        </div>
        ${shouldShowAppFooter() ? appFooter() : ''}
      </div>
    `;
    render(fallbackTemplate, appContainer);
    currentRoute = router.currentPath;
    // Marca o app como carregado mesmo no fallback
    document.body.classList.add('app-loaded');
  }
}

// Register Service Worker for caching and offline support
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Detectar updates disponiveis
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Nova versao disponivel - notificar usuario
                logger.info('New app version available');
                // Disparar evento para UI mostrar toast de update
                window.dispatchEvent(new CustomEvent('sw-update-available'));
              }
            });
          }
        });

        // Check for updates periodically (a cada hora)
        const updateInterval = setInterval(() => {
          registration.update().catch(() => {});
        }, 60 * 60 * 1000);

        // Cleanup interval se pagina for fechada
        window.addEventListener('beforeunload', () => {
          clearInterval(updateInterval);
        });
      })
      .catch((err) => {
        logger.warn('Service worker registration failed:', err.message);
      });
  });
}

renderApp();

// Debounce function to prevent multiple rapid renders
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Handle route changes with debounce using event manager
const debouncedRenderApp = debounce(renderApp, 50);

// Handler para popstate que também restaura scroll quando necessário
const handlePopState = () => {
  debouncedRenderApp();
  // Tentar restaurar posição de scroll (ex: voltando de perfil para lista de clientes)
  router.restoreScrollPosition();
};

window.addEventListener('popstate', handlePopState);

export { renderApp };

window.router = router;

// ==========================================
// Encryption Migration Notification Listener
// Notifica usuário quando tokens v1 são detectados
// ==========================================
let migrationNotificationShown = false;

window.addEventListener('encryption-migration-needed', async (event) => {
  // Evitar notificações duplicadas
  if (migrationNotificationShown) return;
  migrationNotificationShown = true;

  const { v1Count = 0, unencryptedCount = 0 } = event.detail || {};
  const total = v1Count + unencryptedCount;

  if (total === 0) return;

  // Importar sistema de notificações
  const { addNotification } = await import('./components/notifications.js');

  addNotification({
    type: 'warning',
    title: t('notifications.securityUpdate.title'),
    message: t('notifications.securityUpdate.message'),
    duration: 0, // Não auto-dismiss
    actions: [
      {
        text: t('notifications.securityUpdate.migrateNow'),
        primary: true,
        action: () => {
          router.navigate('/settings#integrations');
        }
      },
      {
        text: t('notifications.securityUpdate.later'),
        primary: false,
        action: () => {
          // Apenas fechar - já será removido automaticamente
        }
      }
    ]
  });
});

// Reset flag ao fazer logout
authService.onAuthStateChanged((user) => {
  if (!user) {
    migrationNotificationShown = false;
  }
});